import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, FACEBOOK_URL, INSTAGRAM_URL, WhatsAppIcon } from "@/components/site/ContactLinks";

const LOGO_SRC = "/brand/pazapa-logo.jpg";
const CONTACT_EMAIL = "contact@pazapaasbl.be";

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();
  return (
    <footer className="bg-foreground text-background py-14 md:py-16" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-12">
          <div className="col-span-2 md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={LOGO_SRC} alt="" className="w-14 h-14 rounded-full object-cover bg-white" />
              <div>
                <div className="font-heading text-2xl leading-none">PAZAPA</div>
                <div className="text-xs uppercase tracking-[0.2em] text-background/60 mt-1">
                  {t("footer.since")}
                </div>
              </div>
            </div>
            <p className="mt-6 text-base text-background/80 leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
            <div className="mt-5 text-xs text-background/50 space-y-1">
              <div>PAZAPA ASBL · BCE 0865.041.743</div>
              <div>Rue Piers 93-95-97, 1080 Molenbeek-Saint-Jean</div>
              <div>
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-background/80 underline underline-offset-2" data-testid="footer-email">
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div>
                <a href={PHONE_TEL} className="hover:text-background/80 underline underline-offset-2" data-testid="footer-phone">
                  {PHONE_DISPLAY}
                </a>
              </div>
            </div>
            <div className="mt-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-background/50 mb-3">{t("footer.follow")}</div>
              <div className="flex items-center gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="WhatsApp"
                  data-testid="footer-whatsapp"
                  className="w-10 h-10 rounded-full bg-background/10 hover:bg-background/25 grid place-items-center transition-colors"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                </a>
                {FACEBOOK_URL && (
                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label="Facebook"
                    data-testid="footer-facebook"
                    className="w-10 h-10 rounded-full bg-background/10 hover:bg-background/25 grid place-items-center transition-colors"
                  >
                    <Facebook className="w-5 h-5" strokeWidth={2} />
                  </a>
                )}
                {INSTAGRAM_URL && (
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label="Instagram"
                    data-testid="footer-instagram"
                    className="w-10 h-10 rounded-full bg-background/10 hover:bg-background/25 grid place-items-center transition-colors"
                  >
                    <Instagram className="w-5 h-5" strokeWidth={2} />
                  </a>
                )}
              </div>
            </div>
          </div>

          <FooterCol title={t("footer.col.explore")} items={[
            [t("nav.home"), "/#top"],
            [t("nav.activities"), "/#activites"],
            [t("nav.inclusion"), "/inclusion", true],
            [t("nav.families"), "/#familles"],
            [t("nav.agenda"), "/#agenda"],
          ]} />
          <FooterCol title={t("footer.col.engage")} items={[
            [t("nav.support"), "/#soutenir"],
            [t("support.benevole.title"), "/#soutenir"],
            [t("support.partner.title"), "/#soutenir"],
            [t("support.don.title"), "/#soutenir"],
          ]} />
          <FooterCol title={t("footer.col.contact")} items={[
            [t("nav.talk"), "/#contact"],
            [t("come.chip"), "/#venir"],
          ]} />
        </div>

        <div className="mt-14 pt-8 border-t border-background/15 text-sm text-background/60 md:text-right md:flex md:items-center md:justify-between">
          <span className="block md:inline">{t("footer.made")}</span>
          <span className="block mt-2 md:mt-0">© {year} PAZAPA ASBL — {t("footer.copyright")}</span>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div className="col-span-1 md:col-span-2">
      <div className="text-[10px] uppercase tracking-[0.25em] text-background/50 mb-4">{title}</div>
      <ul className="space-y-2.5">
        {items.map(([label, href, route]) => (
          <li key={label}>
            {route ? (
              <Link to={href} className="text-sm text-background/85 hover:text-white hover:underline underline-offset-4">
                {label}
              </Link>
            ) : (
              <a href={href} className="text-sm text-background/85 hover:text-white hover:underline underline-offset-4">
                {label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
